import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

@Injectable({
    providedIn: 'root',
})
export class ProductService {
    constructor(private datatask: DataTaskService) {}

    /**
     * @datatask 226
     * @datataskName sp_ws_p2_product_save
     * @param params
     * @returns promise containing validation.
     */
    public async saveProduct(params: ProductGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(226, params))[0];
    }

    /**
     * @datatask 215
     * @datataskName sp_ws_p2_product_get
     * @param params
     * @returns promise containing the product with the given prod_keyno/prod_id.
     */
    public async getProduct(params: GetProductParams): Promise<ProductGet> {
        return (await this.datatask.Post(215, params))[0];
    }

    /**
     * @datatask 746
     * @datataskName sp_ws_p2_product_delete
     * @param params
     * @returns promise resolving when no errors.
     */
    public async deleteProduct(params: ProductGet): Promise<void> {
        return await this.datatask.Post(746, params);
    }

    /**
     * Retrieves list of product groups.
     *
     * @datatask 915
     * @datataskName sp_ws_p2_prodgrp_list
     * @params optional configuration for the list.
     * @returns promise containing list.
     */
    public getProductGroups(params?: GetProductGroupsParams): Promise<Item[]> {
        return this.datatask.Post(915, params);
    }

    /**
     * Retrieves list over product types.
     *
     * @datatask 219
     * @datataskName sp_ws_p2_prodtype_list
     * @param params optional configuration for the list.
     * @returns promise containing list.
     */
    public getProductTypes(params?: GetProductTypesParams): Promise<ProductTypeItem[]> {
        return this.datatask.Post(219, params);
    }

    /**
     * Retrieves list over collections.
     *
     * @datatask 1218
     * @datataskName sp_ws_p2_collection_list
     * @param params optional configuration for the list.
     * @returns promise containing list.
     */
    public getCollections(params?: GetCollectionsParams): Promise<Item[]> {
        return this.datatask.Post(1218, params);
    }

    /**
     * @datatask 78
     * @datataskName sp_ws_p2_selectproduct_list
     * @param params
     * @returns promise containing list of products.
     */
    public getProducts(params: GetProductsParams): Promise<ProductItem[]> {
        return this.datatask.Post(78, params);
    }

    /**
     * Retrieves product units for select fields.
     *
     * @datatask 599
     * @datataskName sp_ws_p2_productunit_list_select
     * @param params product to retrieve units for.
     * @returns promise containing list of product units.
     */
    public getProductUnits(params: GetProductUnitsParams): Promise<ProductUnitItem[]> {
        return this.datatask.Post(599, params);
    }

    /**
     * @datatask 3046
     * @datataskName sp_ws_p2_w_productcreate_units_list
     * @param params
     * @return promise containing list of conversion units.
     */
    public getProductCreateConversionUnits(params: GetConversionUnitsParams): Promise<ConversionUnitItem[]> {
        return this.datatask.Post(3046, params);
    }

    public async getProductCreateExisting(params: GetProductCreateExistingParams): Promise<ProductCreateExistingGet> {
        return (await this.datatask.Post(3096, params))[0];
    }

    /**
     * @datatas 3372
     * @datataskName sp_ws_p2_w_productcreate_prodid_no_get
     * @param params
     * @returns promise containing the next number for id.
     */
    public async getProductCreateProdId(params: GetProductCreateProdIdParams): Promise<ProductCreateProdIdGet> {
        return (await this.datatask.Post(3372, params))[0];
    }

    /**
     * @datatask 867
     * @datataskName sp_ws_p2_w_productcreate_calc_salesprice
     * @param params
     * @returns promise containing info of the product to copy from.
     */
    public async getProductToCopyFrom(params: ProductCreateGet): Promise<ProductCreateCopyFromGet | undefined> {
        return (await this.datatask.Post(867, params))[0];
    }

    /**
     * @datatask 672
     * @datataskName sp_ws_p2_w_productcreate_get
     * @param params
     * @returns promise containing product create fields.
     */
    public async getProductCreate(params: GetProductCreateParams): Promise<ProductCreateGet> {
        return (await this.datatask.Post(672, params))[0];
    }

    /**
     * @datatask 677
     * @datatask sp_ws_p2_w_productcreate_create
     * @param params
     * @returns
     */
    public async createProduct(params: CreateProductParams): Promise<CreateProductResponse> {
        return (await this.datatask.Post(677, params))[0];
    }

    /**
     * @datatask 3095
     * @datataskName sp_ws_p2_w_productcreate_calc_salesprice
     * @param params
     * @returns
     */
    public async calcProductCreateSalesPrice(params: ProductCreateGet & { units: ConversionUnitItem[] }): Promise<ProductCreateSalesPrice> {
        return (await this.datatask.Post(3095, params))[0];
    }

    /**
     * @datatask 3831
     * @datataskName sp_ws_p2_pricegrpmethod_list
     * @returns promise containing list of pricegroup methids.
     */
    public getPriceGroupMethods(): Promise<PriceGroupMethodItem[]> {
        return this.datatask.Post(3831);
    }

    /**
     * @datatask 1962
     * @datataskName sp_ws_p2_assortment_list
     * @returns promise containing list of assortments.
     */
    public getAssortments(): Promise<AssortmentItem[]> {
        return this.datatask.Post(1962);
    }

    /**
     * @datatask 3276
     * @datataskName tt_servicetype_list
     * @returns promise containing list of servicetypes.
     */
    public getServiceTypes(): Promise<ServiceTypeItem[]> {
        return this.datatask.Post(3276);
    }

    /**
     * @datatask 2661
     * @datataskName sp_ws_p2_w_product_own_kpi_list
     * @param params
     * @returns promise containing list of kpis.
     */
    public getProductKpiDatas(params: ProductGet): Promise<ProductKpiDataItem[]> {
        return this.datatask.Post(2661, params);
    }

    /**
     * @datatask 3277
     * @datataskName tt_bookinggrp_list
     * @returns promise containing list of booking groups.
     */
    public getBookingGroups(): Promise<BookingGroupItem[]> {
        return this.datatask.Post(3277);
    }

    /**
     * @datatask 1321
     * @datataskName sp_ws_p2_sr_resource_typeahead_list
     * @param params
     * @returns promise containing list of resources.
     */
    public searchResources(params: SearchResourcesParams): Promise<ResourceItem[]> {
        return this.datatask.Post(1321, params);
    }

    /**
     * @datatask 3239
     * @datataskName tt_assemblygroup_save
     * @param params the product to update assembly group on and the assembly group to update till.
     * @returns promise resolving when call has resolved.
     */
    public updateProductAssemblyGroup(params: UpdateProductAssemblyGroupParams): Promise<void> {
        return this.datatask.Post(3239, params);
    }

    /**
     * @datatask 4043
     * @datataskName tt_product_productversion_list
     * @param params the product to retrieve versions of.
     * @returns promise containing list of versions.
     */
    public getProductVersions(params: GetProductVersionsParams): Promise<ProductVersionItem[]> {
        return this.datatask.Post(4043, params);
    }
}

export interface GetProductVersionsParams {
    prod_id: string;
}

export interface ProductVersionItem {
    productversion_keyno: number;
    version_id: string;
    version_description: string;
    prod_id: string;
    reg_datetime: string;
    reg_by_userid: string;
    modified_datetime: string;
    modified_by_userid: string;
}

export interface UpdateProductAssemblyGroupParams {
    prod_id: string;
    assemblygroup_no: string;
}
export interface SearchResourcesParams {
    resource_searchtext: string;
}

export interface ResourceItem {
    item_id: string;
    item_name: string;
}

export interface BookingGroupItem {
    bookinggrp_keyno: string;
    bookinggrp_name: string;
    orderby: string;
}

export interface ProductKpiDataItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_parms: string;
    item_path: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    orderby: string;
}

export interface ServiceTypeItem {
    servicetype_no: string;
    servicetype_name: string;
    orderby: string;
}

export interface AssortmentItem {
    assortment_keyno: string;
    assortment_name: string;
    orderby: string;
}

export interface PriceGroupMethodItem {
    pricegrpmethod_id: string;
    pricegrpmethod_name: string;
    orderby: string;
}

export interface CreateProductResponse {
    errorcode: '1' | '0';
    errormessage: string;
    item_state: string;
    item_parms: string;
}

export interface CreateProductParams extends ProductCreateGet, ProductCreateSalesPrice {
    units: ConversionUnitItem[];
    metacolumns: ProductCreateMetaColumnItem[];
    argtype: string;
    argvalue: string;
}

export interface ProductCreateMetaColumnItem {
    productsettingmc_keyno: string | number;
    elementtype: string;
    isrequired: '1' | '0';
    field_value: string;
}

export interface ProductCreateSalesPrice {
    pricelist_price: string;
    db_pst: string;
    grouptext_purchase: string;
    grouptext_sales_price: string;
    pricelist_price_locval: string;
    cost_locval: string;
    db_amount_locval: string;
    basecurrency_id: string;
    pricelist_valuta_id: string;
}

export interface ProductCreateProdIdGet {
    nextnumber: string;
}

export interface GetProductCreateProdIdParams {
    productsetting_keyno: string;
}

export interface GetProductCreateExistingParams {
    prod_id: string;
}

export interface ProductCreateExistingGet {
    existing_prod_ids: string;
}

export interface GetProductsParams {
    /**
     * @default - the stock place of the current businessco.
     */
    stockplace_id?: string;

    prod_name: string;

    supplier_no?: string | number;

    /**
     * @default '0'
     */
    search_only_this_supplier?: '1' | '0';

    /**
     * @default 0
     */
    businessco_no?: string | number;

    /**
     * @default '-'
     */
    webpage_name?: string;

    /**
     * @default '-'
     */
    parm1?: string;

    /**
     * @default '0'
     */
    onlyonstock?: '1' | '0';

    /**
     * @default 'ALL'
     */
    model_id?: string;

    /**
     * @default 'ALL'
     */
    prodgrp_id?: string;

    /**
     * @default 0
     */
    parm2?: number | string;
}

export interface ProductItem {
    item_id: string;
    item_name: string;
    prod_name: string;
    item_state: string;
    item_parms: string;
    unit_name: string;
    orderby: string;
    unit_no: string | number;
}

export interface GetProductUnitsParams {
    prod_id: string;
}

export interface ProductUnitItem {
    unit_no: string;
    shortname: string;
    orderby: string;
}

export interface GetProductGroupsParams {
    /**
     * @default '0'
     */
    add_all?: '1' | '0';
}

export interface GetProductTypesParams {
    /**
     * @default '0'
     */
    add_all?: '1' | '0';
}

export interface GetCollectionsParams {
    /**
     * @default '0'
     */
    isactive?: '1' | '0';
}

export interface ProductTypeItem {
    prodtype_no: string;
    prodtype_name: string;
    orderby: `${number}`;
}

export interface GetConversionUnitsParams {
    stock_unit_no: string | number;
    stockcount_unit_no: string | number;
    purch_price_unit_no: string | number;
    sales_price_unit_no: string | number;
    purch_number_unit_no: string | number;
}

export interface ConversionUnitItem {
    unit_no: string;
    qtyfactor_name: string;
    unit_no_to: string;
    qtyfactor: string | number;
    orderby: string;
    qtyfactor_name_inverse: string;
    inversevalue: '1' | '0';
    conversion_formula: string;
    conversion_formula_replaced: string;
}

export interface GetProductCreateParams {
    argtype: string;
    argvalue: string;
}

export interface ProductCreateGet {
    prod_id: string;
    prod_name: string;
    prod_name_get: string;
    stock_unit_no: string;
    productsetting_keyno: string;
    prodgrp_id: string;
    prodgrp2_id: string;
    prodgrp3_id: string;
    prodgrp4_id: string;
    businessco_no: string;
    pohcompletionmethod_keyno: string;
    pohcompletionprinciple_keyno: string;
    planpolicy_no: string;
    warning_on_pick_as_component_outside_structure: '1' | '0';
    manage_stockquantity_pr_batch: '1' | '0';
    prodtype_no: string;
    prodplace_id: string;
    product_search: string;
    prod_id_copy_from: string;
    searchtext_supplier: string;
    supplier_no: string;
    suppliers_prodno: string;
    suppliers_prodname: string;
    suppliers_price: string | number;
    suppliers_valuta: string;
    new_number_assigned: '1' | '0';
    is_custactcalc: '1' | '0';
    prodbeomethod_no: string;
    pricelist_id: string;
    pricelist_price: string;
    copy_components: '1' | '0';
    show_custom_check_01: '1' | '0';
    custom_check_01: '1' | '0';
    stockcount_unit_no: string;
    purch_price_unit_no: string;
    sales_price_unit_no: string;
    purch_number_unit_no: string;
    show_unitconversions: '1' | '0';
    show_setting: '1' | '0';
    protected_prod_name: '1' | '0';
    fullprice_val: string;
    purch_discount: string;
    grouptext_purchase: string;
    grouptext_sales_price: string;
    grouptext_unit_conversion: string;
    grouptext_pim_fields: string;
    pricemethod_id: string;
    calc_factor: string | number;
    calc_margin_pst: string | number;
    access_productsetting: '1' | '0';
    db_pst: string;
    basecurrency_id: string;
    pricelist_valuta_id: string;
    drawing_no: string;
    show_drawing_no: '1' | '0';
    prod_designinfo: string;
    show_prod_designinfo: '1' | '0';
    note: string;
    show_note: '1' | '0';
    show_discgrpprod: '1' | '0';
    discgrpprod_keyno: string;
    setting_productsetting_list_filter_by_prodtype: '1' | '0';
    show_productsetting: '1' | '0';
    existing_prod_ids?: string;
}

export interface ProductCreateCopyFromGet {
    prod_name: string;
    productsetting_keyno: string;
    prodtype_no: string;
    prodgrp_id: string;
    prodgrp2_id: string;
    prodgrp3_id: string;
    prodgrp4_id: string;
    supplier_name: string;
    supplier_no: string;
    suppliers_valuta: string;
}

export interface GetProductParams {
    prod_id: string;
}

export interface ProductGet {
    prod_id: string;
    prod_name: string;
    prod_name_short: string;
    show_prod_name_short: '1' | '0';
    prod_stock_unit_no: string;
    prodtype_no: string;
    pohcompletionmethod_keyno: string;
    pohcompletionprinciple_keyno: string;
    stockquantity_pr_batch: string;
    manage_stockquantity_pr_batch: '1' | '0';
    warning_on_pick_as_component_outside_structure: '1' | '0';
    planpolicy_no: string;
    enable_wreck_po: '1' | '0';
    show_button_pictures: '1' | '0';
    show_button_documents: '1' | '0';
    nbr_of_documents: string;
    nbr_of_pictures: string;
    integer_consumption: '1' | '0';
    nbr_of_checklists: string;
    nbr_of_productunits: string;
    edit_unit_stock_possible: '1' | '0';
    prod_stock_unit_name: string;
    activeproduct: '1' | '0';
    prod_text2: string;
    prod_text3: string;
    nbr_of_components: string;
    ean_list: string;
    url_picture: string;
    drawing_no: string;
    show_forecast: '1' | '0';
    prodplace_id: string;
    nbr_of_prodsize: string;
    prodsize_keyno: string;
    prodcycle_no: string;
    prodgrp_id: string;
    prodgrp2_id: string;
    prodgrp3_id: string;
    prodgrp4_id: string;
    custom_key_information1: string;
    custom_key_information2: string;
    custom_key_information3: string;
    show_setting: '1' | '0';
    show_custom_key_information1: '1' | '0';
    show_custom_key_information2: '1' | '0';
    show_custom_key_information3: '1' | '0';
    directpurchase: '1' | '0';
    prodbeomethod_no: string;
    warning_productunit: string;
    model_id: string;
    hide_in_webshop: '1' | '0';
    restrictpossibility: '1' | '0';
    colorgrp_id: string;
    prod_search_name: string;
    show_colorgrp_id: '1' | '0';
    pricegrps_info: string;
    prod_keyno: string;
    show_btn_choices: '1' | '0';
    nbr_of_choices: string;
    collection_keyno: string;
    special_main_resource_id: string;
    special_prod_id_view: string;
    special_prod_name_view: string;
    special_custom_prefix: string;
    sr: '1' | '0';
    frr_id: string;
    nbr_of_custacts: string;
    pricemethod_sum: '1' | '0';
    businessco_no: string;
    customstarif_id: string;
    bg_product_view: string;
    show_btn_spareparts: '1' | '0';
    nbr_of_spareparts: string;
    show_btn_substitutes: '1' | '0';
    nbr_of_substitutes: string;
    show_btn_also_buys: '1' | '0';
    nbr_of_also_buys: string;
    show_btn_fits: '1' | '0';
    nbr_of_fits: string;
    deliverytime_before: string;
    use_est_deliverydate_to_stock: '1' | '0';
    est_deliverydate_to_stock: string;
    assortment_keyno: string;
    place_factor: string;
    show_place_factor: '1' | '0';
    kit_sortorder: string;
    show_kit_sortorder: '1' | '0';
    pofinish_quantity_used_required: '1' | '0';
    product_main_image: string;
    setting_use_external_product_images: '1' | '0';
    require_best_before_date: '1' | '0';
    require_suppliers_batchno: '1' | '0';
    show_require_best_before_date: '1' | '0';
    show_require_suppliers_batchno: '1' | '0';
    show_componentalternatives: '1' | '0';
    nbr_of_componentalternatives: string;
    show_btn_productcalc: '1' | '0';
    thread: string;
    show_thread: '1' | '0';
    days_of_durability: string;
    show_picturebanks: '1' | '0';
    nbr_of_picturebank: string;
    page_heading: string;
    show_custom_check_01: '1' | '0';
    custom_check_01: '1' | '0';
    prod_designinfo: string;
    show_prod_designinfo: '1' | '0';
    note: string;
    show_note: '1' | '0';
    show_discgrpprod: '1' | '0';
    discgrpprod_keyno: string;
    productsetting_keyno: string;
    show_pim_data: '1' | '0';
    show_prod_text2: '1' | '0';
    language_id: string;
    is_dangerousgoods: '1' | '0';
    dangerousgoods_hazardclass_id: string;
    dangerousgoods_tunnelcode_id: string;
    dangerousgoods_un_number: string;
    show_env_tax_pst: '1' | '0';
    environmental_tax_pst: string;
    servicetype_no: string;
    show_servicetype: '1' | '0';
    bookinggrp_keyno: string;
    show_bookinggrp: '1' | '0';
    drawing_rev: string;
    show_waste_pst: '1' | '0';
    waste_pst: string;
    carrier_keyno: string; //RHE20260615 - Parameter for carrier
    setting_show_carrier: '1' | '0'; //RHE20260615 - Added setting
    units_pr_carrier: string;
    show_units_pr_carrier: '1' | '0';
    prod_id_planning: string;
    prod_name_planning: string;
    show_prod_id_planning: '1' | '0';
    setting_can_edit_prodgrp: '1' | '0';
    setting_product_show_prod_text3: '1' | '0';
    setting_product_show_drawing_no: '1' | '0';
    setting_product_show_drawing_rev: '1' | '0';
    show_btn_account_product: '1' | '0';
    show_deviantvat_button: '1' | '0';
    pricegrpmethod_id: string;
    enable_change_pricegrpmethod_id: '1' | '0';
    exclude_from_cost_distribution: '1' | '0';
    bookingunits: string;
    bg_main: string;
}
